/**
 * `useActiveMeTeamId` — resolve a single team id for the `/me/*`
 * surfaces (no `:teamId` in the URL).
 *
 * The §9.1 + §9.2 component-gate calls need a `teamId` to ask
 * `<ComponentGate component="long_range_roster" teamId={…}>`. The
 * worker's active workspace (the same one the chrome's
 * `<WorkspaceSwitcher/>` reflects) is the natural choice — it's the
 * team the worker is currently looking at. Falls back to the first
 * workspace returned by `useWorkspaces()` (recency-ordered server-side
 * per IA-2.1) when no active workspace has been pinned yet.
 *
 * Returns `undefined` while workspaces are loading; the caller passes
 * that through to `<ComponentGate>` whose loading-path renders children
 * optimistically.
 */
import { useEffect, useState } from "react";

import { type WorkspaceRow, useWorkspaces } from "../api/useWorkspaces";
import { getActiveWorkspaceId, subscribeToWorkspaceChanges } from "./activeWorkspace";

export function useActiveMeTeamId(): string | undefined {
  const workspacesQuery = useWorkspaces();
  const [activeId, setActiveId] = useState<string | null>(() => getActiveWorkspaceId());

  useEffect(() => {
    return subscribeToWorkspaceChanges(() => setActiveId(getActiveWorkspaceId()));
  }, []);

  if (!workspacesQuery.data) return undefined;
  const workspaces = workspacesQuery.data;
  const head = workspaces[0];
  if (!head) return undefined;
  if (activeId && workspaces.some((w) => w.team_id === activeId)) {
    return activeId;
  }
  return head.team_id;
}

/**
 * #2071 — the active workspace's row (team name + the caller's role on it),
 * for the `/me/*` surfaces that read differently for a lead: Who's on and
 * the team roster never apply `member_full_roster` to a lead, and the
 * Calendar says when a lead has no shifts of their own. Derived from the
 * workspace list already in cache — no extra round-trip.
 */
export function useActiveMeWorkspace(): WorkspaceRow | undefined {
  const teamId = useActiveMeTeamId();
  const workspacesQuery = useWorkspaces();
  if (!teamId) return undefined;
  return workspacesQuery.data?.find((w) => w.team_id === teamId);
}
